import { Check, ChevronDown, Circle, Loader2 } from "lucide-react";
import { useState } from "react";
import { match } from "ts-pattern";
import { cn } from "@/lib/utils";

interface TaskProgressDisplayProps {
  tasks: Array<{ content: string; status: string; }>;
  className?: string;
}

const TaskProgressDisplay: React.FC<TaskProgressDisplayProps> = ({ tasks, className }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const completedCount = tasks.filter(t => t.status === "completed").length;
  const totalCount = tasks.length;

  return (
    <div className={cn("bg-white rounded-lg border border-gray-200 px-4 py-2", isExpanded && "py-4", className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 h-5">
          <div className="font-semibold">Task progress</div>
          <div className="text-xs text-gray-500">{completedCount} / {totalCount}</div>
        </div>
        <button
          className={cn(
            "h-7 w-7 rounded-md items-center p-1.5 cursor-pointer",
            "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
          )}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <ChevronDown
            className={cn(
              "h-4 w-4",
              !isExpanded && "rotate-180",
            )}
          />
        </button>
      </div>

      {isExpanded && (
        <div className="mt-3 space-y-2">
          {tasks.map((task) => (
            <div key={task.content} className="flex items-start gap-2">
              {match(task.status)
                .with("completed", () => <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />)
                .with(
                  "in_progress",
                  () => <Loader2 className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5 animate-spin" />,
                ).with("pending", () => <Circle className="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5" />)
                .otherwise(() => null)}
              <span
                className={cn(
                  "text-sm",
                  task.status === "completed" && "text-gray-600",
                  task.status === "in_progress" && "text-gray-900 font-medium",
                  task.status === "pending" && "text-gray-400",
                )}
              >
                {task.content}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TaskProgressDisplay;
